import { MapPin, Phone, ExternalLink } from "lucide-react";

export const AdoptableCard = ({ dog, index }) => (
  <a
    href={dog.shelter_url || "#"}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`adoptable-card-${index}`}
    className="group rounded-3xl overflow-hidden bg-card border border-border/60 warm-shadow hover:-translate-y-1 transition-transform flex flex-col"
  >
    <div className="aspect-[4/3] bg-muted overflow-hidden">
      {dog.photo && (
        <img
          src={dog.photo}
          alt={dog.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
        />
      )}
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <div className="flex items-baseline gap-2">
        <h3 className="font-display font-bold text-lg text-accent flex-1 truncate">{dog.name}</h3>
        {dog.sex && (
          <span className="text-xs font-semibold text-primary">{dog.sex === "M" ? "♂" : dog.sex === "F" ? "♀" : dog.sex}</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">
        {dog.breed_text}
        {dog.age_years != null && <> · {dog.age_years < 1 ? `${Math.round(dog.age_years * 12)} mo` : `${Math.round(dog.age_years)} y`}</>}
      </p>
      {dog.description && (
        <p className="text-xs text-accent/70 mt-2 leading-relaxed line-clamp-3">{dog.description}</p>
      )}
      <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1 truncate">
          <MapPin size={11} /> {dog.shelter_name || dog.state}
        </span>
        <ExternalLink size={11} className="text-primary shrink-0" />
      </div>
    </div>
  </a>
);

export const ShelterCard = ({ shelter, index, userState }) => (
  <a
    href={shelter.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`shelter-card-${index}`}
    className={`group rounded-3xl border border-border/60 warm-shadow p-6 hover:-translate-y-1 transition-transform bg-card flex flex-col justify-between ${
      shelter.state === userState && userState ? "ring-2 ring-primary/40" : ""
    }`}
  >
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
          shelter.state === "national"
            ? "bg-accent text-accent-foreground"
            : "bg-secondary text-secondary-foreground"
        }`}>
          {shelter.state === "national" ? "National" : shelter.state}
        </span>
        {shelter.state === userState && userState && (
          <span className="text-[10px] uppercase tracking-wider font-bold text-primary">
            Near you
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-xl text-accent">{shelter.name}</h3>
      <p className="text-xs text-muted-foreground mt-1">{shelter.city}</p>
      <p className="text-sm text-accent/80 mt-3 leading-relaxed">{shelter.description}</p>
    </div>
    <div className="mt-5 flex items-center justify-between text-sm">
      {shelter.phone ? (
        <span className="text-muted-foreground inline-flex items-center gap-1">
          <Phone size={12} /> {shelter.phone}
        </span>
      ) : <span />}
      <span className="text-primary font-semibold inline-flex items-center gap-1">
        Visit <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </div>
  </a>
);

export const TrainerCard = ({ trainer, index }) => (
  <a
    href={trainer.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`trainer-card-${index}`}
    className="group rounded-3xl border border-border/60 warm-shadow p-6 hover:-translate-y-1 transition-transform bg-card"
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-accent">
        {trainer.coverage}
      </span>
    </div>
    <h3 className="font-display font-bold text-xl text-accent">{trainer.name}</h3>
    <p className="text-sm text-accent/80 mt-2 leading-relaxed">{trainer.description}</p>
    <div className="mt-4 flex flex-wrap gap-1.5">
      {trainer.services.map((s) => (
        <span key={s} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
          {s}
        </span>
      ))}
    </div>
    <div className="mt-5 flex items-center justify-between text-sm">
      {trainer.phone ? (
        <span className="text-muted-foreground inline-flex items-center gap-1">
          <Phone size={12} /> {trainer.phone}
        </span>
      ) : <span />}
      <span className="text-primary font-semibold inline-flex items-center gap-1">
        Find a trainer <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </div>
  </a>
);
